import {
  useCreateDispatchActions,
  GarminConnectDataDigest,
  GarminDirectDataDigest,
  useSelectParticipantAdherence,
  SelectedCellIndex,
  useSelectMethod,
  useSelectParticipantDataDigest,
  useSelectTheme,
  ThemeState,
  useSelectParticipantGarminDirectDataDigest,
  useSelectGarminDirectDateDigestCoveredIndexSet,
  useSelectParticipantGarminConnectDataDigest,
  useSelectGarminConnectDateDigestCoveredIndexSet,
  useSelectParticipantAdherenceDigest,
} from "../../store";
import {
  AdherenceType,
  AdherenceViewedByType,
  AdherenceDeviceDataTypeCollectStateEntry,
  AdherenceGarminConnectWearTimeElement,
  AdherenceGarminDirectBbiDigestElement,
  AdherenceTasksCompleteStateEntry,
} from "..";
import { RIF, t } from "../../lib";
import {
  AdherenceViewTag,
  DigestDay,
  GarminDirect,
  GarminDirectBackendKeyMap,
  TaskStateType,
} from "../../model";
import {
  DisableBoxIcon,
  NotJoinedBG,
  HeatmapLowWearTimeBG,
  HeatmapLowWearTimeGCBG,
  HeatmapLowWearTimeGCLFBG,
} from "../../asset/image";
import { IParticipant, ITask, TaskType } from "../../shared/db";
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Lottie from "lottie-web";
import loadingAnimation from "../../asset/lotties/loading_animation.json";
import { Interpolation } from "@emotion/react";
import { Theme } from "react-select";
import React from "react";
import { createContext as createContextSelector, useContextSelector } from "use-context-selector";
import moment from "moment";

export interface CellContextType {
  isScheduleProject: boolean;
  adherenceType: string;
  gridData: GridData;
  cellSize: CellSize;
  viewTagList: AdherenceViewTag[];
}

export interface GridData {
  digestDateList: DigestDate[];
  participantList: IParticipant[];
}

export interface DigestDate {
  day: string;
  dayDate: string;
  dayUnixTimestamp: number;
  yymmddIndex: number;
}

export interface CellSize {
  width: number;
  height: number;
}

export const CellContext = React.createContext<CellContextType | undefined>(undefined);

type SelectedCellContextType = {
  selectedCellIndex: SelectedCellIndex | null;
  setSelectedCellIndex: (index: SelectedCellIndex | null) => void;
};

export const SelectedCellContext = createContextSelector<SelectedCellContextType | undefined>(
  undefined,
);

export const SelectedCellContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedCellIndex, setSelectedCellIndex] = useState<SelectedCellIndex | null>(null);
  const value = { selectedCellIndex, setSelectedCellIndex };

  return <SelectedCellContext.Provider value={value}>{children}</SelectedCellContext.Provider>;
};

const getBackgroundColorByDataMinutes = (dataMinutes: number): string => {
  const twoHoursInMinutes = 120;
  if (dataMinutes <= twoHoursInMinutes) return "#FFFFFF";
  else if (dataMinutes <= twoHoursInMinutes * 2) return "#F1AA8C";
  else if (dataMinutes <= twoHoursInMinutes * 4) return "#F1D591";
  else if (dataMinutes <= twoHoursInMinutes * 6) return "#F0ED92";
  else if (dataMinutes <= twoHoursInMinutes * 8) return "#D8EA9A";
  else if (dataMinutes <= twoHoursInMinutes * 10) return "#C7E59E";
  else return "#8ED3AA";
};
// calculate the background color of heatmap
const MILLISECONDS_IN_ONE_MINUTE = 60000;
const getElementBackgroundColor = (
  adherenceType: string,
  digestDay: DigestDay,
  index: number,
  highLightBox: string,
  garminConnectDataDigest?: GarminConnectDataDigest | null,
  garminDirectDataDigest?: GarminDirectDataDigest | null,
): string => {
  if (adherenceType === AdherenceType.GarminDirectBbiDigest) {
    if (!garminDirectDataDigest) {
      return "#FAFAFA";
    }
    const dataMinutes = (garminDirectDataDigest?.wearTime ?? 0) / MILLISECONDS_IN_ONE_MINUTE;
    return getBackgroundColorByDataMinutes(dataMinutes);
  } else if (adherenceType === AdherenceType.GarminConnectWearTime) {
    if (!garminConnectDataDigest) {
      return "#FAFAFA";
    }
    const dataMinutes = garminConnectDataDigest.wearTime ?? 0;
    return getBackgroundColorByDataMinutes(dataMinutes);
  }
  return (highLightBox === digestDay?._id ? "#3455F414" : index % 2 !== 0 && "#FCFCFC") as string;
};

const MainStateContent = (props: {
  adherenceViewedByType?: AdherenceViewedByType;
  digestDay: DigestDay;
  isScheduleProject?: boolean;
  viewTagList: AdherenceViewTag[];
  garminDirect?: ITask;
  theme: ThemeState;
  viewDatatype?: GarminDirect;
  garminConnectDataDigest?: GarminConnectDataDigest | null;
  garminDirectDataDigest?: GarminDirectDataDigest | null;
  isScrolling?: boolean;
}) => {
  const {
    adherenceViewedByType,
    digestDay,
    isScheduleProject,
    viewTagList,
    garminDirect,
    theme,
    viewDatatype,
    garminDirectDataDigest,
    garminConnectDataDigest,
    isScrolling,
  } = props;
  const { adherenceType } = useParams();
  const { color } = theme;
  switch (adherenceType) {
    case AdherenceType.Overview: {
      switch (adherenceViewedByType) {
        case AdherenceViewedByType.Tasks:
          return (
            <AdherenceTasksCompleteStateEntry
              digestDay={digestDay}
              isScheduleProject={isScheduleProject}
              viewTagList={isScrolling == true ? [] : viewTagList}
              garminDirect={garminDirect}
              theme={theme}
              garminConnectDataDigest={garminConnectDataDigest}
              garminDirectDataDigest={garminDirectDataDigest}
            />
          );
        case AdherenceViewedByType.DeviceDataType:
          return (
            <AdherenceDeviceDataTypeCollectStateEntry
              digestDay={digestDay}
              color={color}
              dataTypeToView={viewDatatype ? GarminDirectBackendKeyMap[viewDatatype] : undefined}
            />
          );
        default:
          return <></>;
      }
    }
    case AdherenceType.GarminDirectBbiDigest:
      return (
        <AdherenceGarminDirectBbiDigestElement
          {...{
            garminDirectDataDigest,
            color,
          }}
        />
      );
    case AdherenceType.GarminConnectWearTime:
      return (
        <AdherenceGarminConnectWearTimeElement
          {...{
            garminConnectDataDigest,
            color,
          }}
        />
      );
    default:
      return <></>;
  }
};

interface AdherenceDigestDayProps {
  participantIndex: number;
  digestDateIndex: number;
  projectId?: string;
  adherenceViewedByType?: AdherenceViewedByType;
  highLightBox: string;
  setHighLightBox?: Dispatch<SetStateAction<string>>;
  handleClickCell?: (participantId: string, yymmddIndex: number) => void;
}

export const AdherenceDigestDay = (props: AdherenceDigestDayProps) => {
  const cellContext = useContext(CellContext);
  if (!props.projectId || !cellContext) return <></>;
  return (
    <AdherenceDigestDayCell {...props} projectId={props.projectId} cellContext={cellContext} />
  );
};

const AdherenceDigestDayCell = (
  props: AdherenceDigestDayProps & { projectId: string; cellContext: CellContextType },
) => {
  const {
    participantIndex,
    digestDateIndex,
    projectId,
    adherenceViewedByType,
    highLightBox,
    setHighLightBox,
    handleClickCell,
    cellContext,
  } = props;
  // console.log('render cell') // test code

  const theme = useSelectTheme();
  const { color, fontWeight, fontSize } = theme;
  const method = useSelectMethod();
  const garminDirect = method?.taskList?.find(
    (item: TaskStateType) => item.type === TaskType.GarminDevice,
  );

  const { doREQUEST_REQUEST_PARTICIPANT_DAILY_DATA_DIGEST } = useCreateDispatchActions();
  const animRef = useRef<HTMLDivElement>(null);

  const { isScheduleProject, adherenceType, gridData, cellSize, viewTagList } = cellContext;
  const participant = gridData.participantList[participantIndex];
  const participantId = participant?.id ?? "";
  const adherence = useSelectParticipantAdherence(projectId, participantId);
  const participantDataDigest = useSelectParticipantDataDigest(participantId);

  const emptyDigestDay = gridData.digestDateList[digestDateIndex];
  const yymmddIndex = emptyDigestDay.yymmddIndex;
  const adhereceDigest = useSelectParticipantAdherenceDigest(
    projectId,
    participantId,
    yymmddIndex.toString(),
  );
  const digestDay = useMemo(() => {
    if (!adhereceDigest) return emptyDigestDay;
    else return adhereceDigest;
  }, [adhereceDigest]);

  const index = digestDateIndex;
  const showRightBorder = digestDateIndex !== gridData.digestDateList.length - 1 || false;

  const garminDirectDataDigestState = useSelectParticipantGarminDirectDataDigest(
    projectId,
    participantId,
    yymmddIndex.toString(),
  );
  const garminDirectDataDigestCoveredYymmddIndexSet =
    useSelectGarminDirectDateDigestCoveredIndexSet(projectId);

  const garminConnectDataDigestState = useSelectParticipantGarminConnectDataDigest(
    projectId,
    participantId,
    yymmddIndex.toString(),
  );
  const garminConnectDataDigestCoveredYymmddIndexSet =
    useSelectGarminConnectDateDigestCoveredIndexSet(projectId);

  // const debugCell = () => {
  //   return (
  //     <div
  //       css={{
  //         border: '1px solid #ff00FF',
  //         boxSizing: 'border-box',
  //         borderRight: showRightBorder ? `1px solid ${color.grey_100}` : 'none',
  //         borderBottom: `1px solid ${color.grey_100}`,
  //         borderColor: color.grey_100,
  //         backgroundColor: color.surface.white,
  //         minWidth: cellSize.width,
  //         width: cellSize.width,
  //         cursor: 'pointer',
  //         minHeight: cellSize.height,
  //         height: cellSize.height,
  //       }}
  //     >{`${digestDateIndex}, ${participantIndex}`}</div>
  //   )
  // }
  // return debugCell()

  const participantActivatedYymmdd = useMemo(() => {
    return t.getYymmddFromIsoAndTimezone(
      participant?.activatedDate,
      participant?.activatedTimezone,
    );
  }, [participant?.id]);

  const getRelativeDayDiff = (yymmdd: number) => {
    if (participantActivatedYymmdd) {
      const diffDays = t.getYymmddDiffInDays(participantActivatedYymmdd, yymmdd);
      return diffDays + 1;
    }
    return;
  };

  const activated =
    participantActivatedYymmdd && digestDay.yymmddIndex >= participantActivatedYymmdd;
  const setSelectedCellIndex = useContextSelector(
    SelectedCellContext,
    (v) => v?.setSelectedCellIndex,
  );

  const selectedCellIndex = useContextSelector(SelectedCellContext, (v) => v?.selectedCellIndex);
  const selected = useMemo(() => {
    return (
      activated &&
      selectedCellIndex?.yymmddIndex === digestDay.yymmddIndex &&
      selectedCellIndex?.participantId === adherence?.participantId
    );
  }, [selectedCellIndex]);

  const handleClick = (element: DigestDay) => {
    // TODO: wait mobile, last seen time?
    const activated =
      participantActivatedYymmdd && element.yymmddIndex >= participantActivatedYymmdd;
    const isValid = activated || element.garminConnectSynced || element.garminDirectCollected;
    if (!isValid) return;

    if (adherence) {
      const participantId = adherence.participantId;
      const yymmddIndex = element.yymmddIndex;
      setSelectedCellIndex?.({ participantId, yymmddIndex });

      handleClickCell?.(participantId, yymmddIndex);
      const digestDay = adherence?.digestDayMap?.[yymmddIndex];
      // check if participantDataDigest exists
      if (digestDay && !participantDataDigest?.[yymmddIndex]) {
        doREQUEST_REQUEST_PARTICIPANT_DAILY_DATA_DIGEST({
          payload: {
            projectId,
            participantId,
            yymmddIndex: element.yymmddIndex,
            requestGarminDirectData: element.garminDirectCollected === true,
            requestGarminConnectData: element.garminConnectSynced === true,
            requestDexcomData: element.dexcomSynced === true,
          },
        });
      }
    }

    // TODO: careful about "_id" (not official from BE)
    if (!element._id) return;
    setHighLightBox?.(element._id);
  };

  const isYymmddBeforeLastSync = (lastSync?: number) => {
    if (!lastSync) return false;
    const timezone = participant?.activatedTimezone || "UTC";
    const lastSyncYymmdd = moment.tz(new Date(lastSync), timezone).format("yMMDD").slice(2);
    return digestDay.yymmddIndex <= +lastSyncYymmdd;
  };

  const garminConnectSynced = useMemo(() => {
    return isYymmddBeforeLastSync(adherence?.status.unixTimestampLastGarminConnectSync);
  }, [adherence]);

  const garminDirectSynced = useMemo(() => {
    return isYymmddBeforeLastSync(adherence?.status.unixTimestampLastGarminDeviceSync);
  }, [adherence]);

  const synced = useMemo(() => {
    switch (adherenceType) {
      case AdherenceType.GarminDirectBbiDigest:
        return garminDirectSynced;
      case AdherenceType.GarminConnectWearTime:
        return garminConnectSynced;
      case AdherenceType.GCLFCombined:
        return garminConnectSynced || garminDirectSynced;
      default:
        return true;
    }
  }, [adherenceType, garminDirectSynced, garminConnectSynced]);

  const fetchGarminDirectDataDigest = useMemo(() => {
    return (
      synced && activated && adherenceType !== AdherenceType.GarminConnectWearTime // only exlucde fetch when GarminConnectWearTime is selected
    );
  }, [synced, activated, adherenceType]);

  const fetchGarminConnectDataDigest = useMemo(() => {
    return (
      synced && activated && adherenceType !== AdherenceType.GarminDirectBbiDigest // only exlucde fetch when GarminDirectBbiDigest is selected
    );
  }, [synced, activated, adherenceType]);

  const garminDirectDataDigest = useMemo(() => {
    if (!fetchGarminDirectDataDigest) return null;

    if (garminDirectDataDigestState) {
      return garminDirectDataDigestState;
    } else {
      return garminDirectDataDigestCoveredYymmddIndexSet?.has(yymmddIndex) ? null : undefined;
    }
  }, [
    fetchGarminDirectDataDigest,
    garminDirectDataDigestState,
    garminDirectDataDigestCoveredYymmddIndexSet,
  ]);

  const garminConnectDataDigest = useMemo(() => {
    if (!fetchGarminConnectDataDigest) return null;

    if (garminConnectDataDigestState) {
      return garminConnectDataDigestState;
    } else {
      return garminConnectDataDigestCoveredYymmddIndexSet?.has(yymmddIndex) ? null : undefined;
    }
  }, [
    fetchGarminConnectDataDigest,
    garminConnectDataDigestState,
    garminConnectDataDigestCoveredYymmddIndexSet,
  ]);

  // loading animation
  const isLoading = useMemo(() => {
    if (synced) {
      switch (adherenceType) {
        case AdherenceType.GarminDirectBbiDigest:
          return garminDirectDataDigest === undefined;
        case AdherenceType.GarminConnectWearTime:
          return garminConnectDataDigest === undefined;
        case AdherenceType.GCLFCombined:
          return garminConnectDataDigest === undefined || garminDirectDataDigest === undefined;
        default:
          return false;
      }
    }
    return false;
  }, [synced, adherenceType, garminDirectDataDigest, garminConnectDataDigest]);

  useEffect(() => {
    if (!animRef.current) return;
    const anim = Lottie.loadAnimation({
      container: animRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: loadingAnimation,
    });
    return () => anim.stop();
  }, [isLoading]);

  const MILLISECONDS_IN_ONE_HOUR = 3600000;
  const MINUTES_IN_ONE_HOUR = 60;
  const { wearTime, wearTimeHours } = useMemo(() => {
    switch (adherenceType) {
      case AdherenceType.GarminDirectBbiDigest: {
        const wearTime = garminDirectDataDigest?.wearTime ?? 0;
        return { wearTime, wearTimeHours: Math.floor(wearTime / MILLISECONDS_IN_ONE_HOUR) };
      }
      case AdherenceType.GarminConnectWearTime: {
        const wearTime = garminConnectDataDigest?.wearTime ?? 0;
        return { wearTime, wearTimeHours: Math.floor(wearTime / MINUTES_IN_ONE_HOUR) };
      }
      default:
        return { wearTime: 0, wearTimeHours: 0 };
    }
  }, [adherenceType, garminConnectDataDigest, garminDirectDataDigest]);

  // combined wear time for GCLFCombined
  const combinedWearTime = useMemo(() => {
    if (adherenceType === AdherenceType.GCLFCombined) {
      return [
        Math.floor((garminConnectDataDigest?.wearTime ?? 0) / MINUTES_IN_ONE_HOUR),
        Math.floor((garminDirectDataDigest?.wearTime ?? 0) / MILLISECONDS_IN_ONE_HOUR),
      ];
    }
    return [0, 0];
  }, [adherenceType, garminConnectDataDigest, garminDirectDataDigest]);

  const wearTimeColors = useMemo(() => {
    if (wearTimeHours > 4) {
      if (adherenceType === AdherenceType.GarminDirectBbiDigest) {
        if (wearTimeHours <= 8) {
          return color.heatmap.lf._1;
        } else if (wearTimeHours <= 12) {
          return color.heatmap.lf._2;
        } else if (wearTimeHours <= 16) {
          return color.heatmap.lf._3;
        } else if (wearTimeHours <= 20) {
          return color.heatmap.lf._4;
        } else {
          return color.heatmap.lf._5;
        }
      } else if (adherenceType === AdherenceType.GarminConnectWearTime) {
        if (wearTimeHours <= 8) {
          return color.heatmap.gc._1;
        } else if (wearTimeHours <= 12) {
          return color.heatmap.gc._2;
        } else if (wearTimeHours <= 16) {
          return color.heatmap.gc._3;
        } else if (wearTimeHours <= 20) {
          return color.heatmap.gc._4;
        } else {
          return color.heatmap.gc._5;
        }
      }
    }
    return color.surface.white;
  }, [wearTimeHours, adherenceType]);

  const combinedWeartimeColors = useMemo(() => {
    if (adherenceType === AdherenceType.GCLFCombined) {
      const result = [];
      // combinedWearTime[0] is Garmin Connect wear time
      if (combinedWearTime[0] <= 4) {
        result.push("transparent");
      } else if (combinedWearTime[0] <= 8) {
        result.push(color.heatmap.mix._1);
      } else if (combinedWearTime[0] <= 12) {
        result.push(color.heatmap.mix._2);
      } else if (combinedWearTime[0] <= 16) {
        result.push(color.heatmap.mix._3);
      } else if (combinedWearTime[0] <= 20) {
        result.push(color.heatmap.mix._4);
      } else {
        result.push(color.heatmap.mix._5);
      }
      // combinedWearTime[1] is Garmin Direct wear time
      if (combinedWearTime[1] <= 4) {
        result.push("transparent");
      } else if (combinedWearTime[1] <= 8) {
        result.push(color.heatmap.mix._1);
      } else if (combinedWearTime[1] <= 12) {
        result.push(color.heatmap.mix._2);
      } else if (combinedWearTime[1] <= 16) {
        result.push(color.heatmap.mix._3);
      } else if (combinedWearTime[1] <= 20) {
        result.push(color.heatmap.mix._4);
      } else {
        result.push(color.heatmap.mix._5);
      }
      return result;
    }
    return [color.surface.white, color.surface.white];
  }, [combinedWearTime, adherenceType]);

  const wearTimeImage = useMemo(() => {
    if (wearTime > 0 && wearTimeHours <= 4) {
      if (adherenceType === AdherenceType.GarminDirectBbiDigest) {
        return `url(${HeatmapLowWearTimeBG})`;
      } else if (adherenceType === AdherenceType.GarminConnectWearTime) {
        return `url(${HeatmapLowWearTimeGCBG})`;
      }
    }
    return "none";
  }, [wearTime, wearTimeHours, adherenceType]);

  const overViewCell = () => {
    /* not activated date */
    if (!activated) {
      return (
        <div
          css={{
            minWidth: cellSize.width,
            height: "100%",
            backgroundImage: `url(${DisableBoxIcon})`,
            backgroundColor: color.surface.white,
            backgroundPosition: "0 0",
            backgroundSize: "contain",
          }}
        />
      );
    } else {
      const relativeDayDiff = getRelativeDayDiff(digestDay.yymmddIndex);
      return (
        <div
          css={{
            width: "100%",
            height: "100%",
            backgroundColor: getElementBackgroundColor(
              adherenceType,
              digestDay,
              index,
              highLightBox,
              garminConnectDataDigest,
              garminDirectDataDigest,
            ),
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: "16px",
            padding: "0 24px",
            flexWrap: "wrap",
            borderRadius: "5px",
            border: `2px solid ${selected ? color.primary : "transparent"}`,
            boxSizing: "border-box",
            ":hover": {
              border: `2px solid ${color.primary}`,
            },
          }}
        >
          <span
            css={{
              position: "absolute",
              left: "8px",
              top: "2px",
              color: color.textIcon.greyIcon,
              fontSize: "8px",
              fontWeight: fontWeight.bold,
            }}
          >
            {RIF(relativeDayDiff != undefined, `Day ${relativeDayDiff}`)}
          </span>
          <MainStateContent
            digestDay={digestDay}
            isScheduleProject={isScheduleProject}
            adherenceViewedByType={adherenceViewedByType}
            theme={theme}
            viewTagList={viewTagList}
            garminDirect={garminDirect}
            // viewDatatype={viewDatatype}
            garminConnectDataDigest={garminConnectDataDigest}
            garminDirectDataDigest={garminDirectDataDigest}
            // participantDataDigest={participantDataDigest[adherence?.participantId ?? '']?.[digestDay.yymmddIndex]}
          />
        </div>
      );
    }
  };

  const garminWearTimeCell = () => {
    if (isLoading) {
      return (
        <div
          ref={animRef}
          css={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      );
    } else {
      if (synced) {
        return (
          <div
            css={{
              width: "100%",
              height: "100%",
              backgroundColor: wearTimeColors,
              borderRadius: 8,
              border: wearTime === 0 ? `1px solid ${color.textIcon.greyIcon}` : "none",
              color: color.textIcon.secondary,
              fontSize: fontSize.h8,
              fontWeight: fontWeight.thick,
              backgroundImage: wearTimeImage,
              backgroundSize: "contain",
              backgroundPosition: "center",
              lineHeight: "11px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {wearTime === 0
              ? `No ${adherenceType === AdherenceType.GarminDirectBbiDigest ? "BBI" : "HR"} Data`
              : ""}
          </div>
        );
      } else {
        return (
          <span
            css={{
              color: color.textIcon.description,
              fontSize: fontSize.h9,
              fontStyle: "italic",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            Not synced yet
          </span>
        );
      }
    }
  };

  const garminCombinedWearTimeCell = () => {
    if (isLoading) {
      // loading animation will effect the view size even after state changed
      // don't know why now, just leave it for now (2025/8/21)
      return (
        <div
          ref={animRef}
          css={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      );
    } else if (synced) {
      return (
        <>
          <div
            css={{
              height: "100%",
              width: "100%",
              padding: "4px 4px 4px 4px",
              // backgroundColor: combinedWearTime ? color.surface.white : color.surface.nested,
              display: "flex",
            }}
          >
            <div
              css={{
                width: "50%",
                height: "100%",
                backgroundColor: combinedWeartimeColors[0],
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                border: combinedWearTime[0] === 0 ? `1px solid ${color.textIcon.greyIcon}` : "none",
                borderRight: "none",
                backgroundImage:
                  combinedWearTime[0] <= 4 && combinedWearTime[0] > 0
                    ? `url(${HeatmapLowWearTimeGCLFBG})`
                    : "none",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                lineHeight: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  color: color.textIcon.secondary,
                  fontSize: fontSize.h8,
                  fontWeight: fontWeight.thick,
                  whiteSpace: "nowrap",
                  transform: "rotate(90deg)",
                }}
              >
                {combinedWearTime[0] === 0 ? "No HR" : " "}
              </span>
            </div>

            {combinedWearTime[0] === 0 && combinedWearTime[1] === 0 && (
              <div
                css={{
                  width: 0,
                  height: "100%",
                  borderLeft: `1px solid ${color.textIcon.greyIcon}`,
                }}
              />
            )}

            <div
              css={{
                width: "50%",
                height: "100%",
                backgroundColor: combinedWeartimeColors[1],
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
                border: combinedWearTime[1] === 0 ? `1px solid ${color.textIcon.greyIcon}` : "none",
                borderLeft: "none",
                backgroundImage:
                  combinedWearTime[1] <= 4 && combinedWearTime[1] > 0
                    ? `url(${HeatmapLowWearTimeGCLFBG})`
                    : "none",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                lineHeight: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  color: color.textIcon.secondary,
                  fontSize: fontSize.h8,
                  fontWeight: fontWeight.thick,
                  whiteSpace: "nowrap",
                  transform: "rotate(90deg)",
                }}
              >
                {combinedWearTime[1] === 0 ? "No BBI" : ""}
              </span>
            </div>
          </div>
        </>
      );
    } else {
      return (
        <span
          css={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: 4,
            color: color.textIcon.description,
            fontSize: fontSize.h9,
            fontStyle: "italic",
          }}
        >
          Not synced yet
        </span>
      );
    }
  };

  const overviewCellCss: Interpolation<Theme> = {
    boxSizing: "border-box",
    borderRight: showRightBorder ? `1px solid ${color.grey_100}` : "none",
    borderBottom: `1px solid ${color.grey_100}`,
    borderColor: color.grey_100,
    backgroundColor: color.surface.white,
    minWidth: cellSize.width,
    width: cellSize.width,
    cursor: "pointer",
    minHeight: cellSize.height,
    height: cellSize.height,
  };

  const garminWearTimeCellCss: Interpolation<Theme> = {
    boxSizing: "border-box",
    borderRight: showRightBorder ? `1px solid ${color.border.light}` : "none",
    borderBottom: `1px solid ${color.border.light}`,
    backgroundColor: activated
      ? synced
        ? color.surface.white
        : color.surface.nested
      : "transparent",
    minWidth: 40,
    width: 40,
    cursor: "pointer",
    minHeight: cellSize.height,
    height: cellSize.height,
    backgroundImage: !activated ? `url(${NotJoinedBG})` : undefined,
    padding: 4,
    display: "flex",
    alignItems: "center",
    borderRadius: 8,
    border: selected ? `2px solid ${color.primary}` : "none",
  };

  const garminCombinedWearTimeCellCss: Interpolation<Theme> = {
    boxSizing: "border-box",
    borderRight: showRightBorder ? `1px solid ${color.border.light}` : "none",
    borderBottom: `1px solid ${color.border.light}`,
    backgroundImage: !activated ? `url(${NotJoinedBG})` : undefined,
    backgroundColor: activated && !synced ? color.surface.nested : color.surface.white,
    minWidth: 40,
    width: 40,
    cursor: "pointer",
    minHeight: cellSize.height,
    height: cellSize.height,
    display: "flex",
    alignItems: "center",
    borderRadius: 8,
    border: selected ? `2px solid ${color.primary}` : "none",
  };

  const digestDayCell = () => {
    switch (adherenceType) {
      case AdherenceType.Overview: {
        return (
          <div
            key={`dd-${index}`}
            onClick={() => {
              handleClick(digestDay);
            }}
            css={overviewCellCss}
          >
            {overViewCell()}
          </div>
        );
      }

      case AdherenceType.GarminDirectBbiDigest:
      case AdherenceType.GarminConnectWearTime: {
        return (
          <div
            key={`dd-${index}`}
            onClick={() => handleClick(digestDay)}
            css={garminWearTimeCellCss}
          >
            {activated && garminWearTimeCell()}
          </div>
        );
      }

      case AdherenceType.GCLFCombined: {
        return (
          <div
            key={`dd-${index}`}
            onClick={() => handleClick(digestDay)}
            css={garminCombinedWearTimeCellCss}
          >
            {activated && garminCombinedWearTimeCell()}
          </div>
        );
      }

      default:
        return <div key={`dd-${index}`} onClick={() => handleClick(digestDay)} />;
    }
  };

  return digestDayCell();
};
